import _ from "lodash";
import React from "react";
import classNames from "classnames";

// components
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import RubricTable from "Rubric/components/RubricTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import ChecklistTable from "Checklist/components/ChecklistTable";
import MYPObjectiveRubric from "UnitPlans/components/IBcomponents/AssessmentTool/MYPObjectiveRubric";
import CriteriaRubricAssessmentWrapper from "UnitPlans/components/IBcomponents/CriteriaRubricAssessmentWrapper";
import StandardRubric from "AppComponents/StandardRubric";
import AssessmentIBScaleTool from "UnitPlans/components/IBcomponents/AssessmentIBScaleTool";
import AssessmentGradeScaleTool from "AppComponents/AssessmentGradeScaleTool";

// design system
import { MediaCard, Button } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  CommentRemarkOutlined,
  PencilLineOutlined,
  SortOutlined,
} from "@toddle-edu/ds-icons";
import { fontStyles } from "@toddle-edu/ds-theme";

// modules / utils
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  getShowAssessmentTool,
  reorderTools,
  getAssessmentToolText,
  getAssessmentToolTypeForMediaCard,
  getIsIBGradeScaleTool,
  getIsEvaluationDone,
  getStaffElementRemarkMessage,
  getAssessmentToolRevampedViewModeStyles,
  getAssessmentToolSelfEvaluationModeStyles,
  getScoreValueElementRatings,
  getIsEvaluationApplicable,
} from "ClassRoom/modules/utils";
import { ASSESSMENT_TOOL_ICON_MAPPING } from "ClassRoom/modules/Constants";
import {
  getIsCurrentAcademicYearSelected,
  getIsParent,
  getUserType,
} from "modules/CommonSelectors";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { getStudentScore } from "ClassProgressSummaryV2/modules/Utils";
import { getMYPObjectiveRubricOptions } from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import { handleAssessmentToolResponseInCache } from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { getAssessmentToolItem } from "Utils/AssessmentToolHelpers";

// constants
import {
  SCORE,
  COMMENT,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  DP_ASSESSMENT_RUBRIC,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";

// third party
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

// scss
import classes from "./StudentMeasureAssessing.scss";

const TOOLS_ORDER = [SCORE, "IB_GRADE_SCALE", COMMENT, "REMAINING_TOOLS"];
const TOOLS_WITH_LABEL_RENDERED_INSIDE_COMPONENT = [
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  DP_ASSESSMENT_RUBRIC,
];

const styles = {
  toolStyles: {
    leftContainerStyle: {
      fontSize: "1.4rem",
      ...fontStyles.demiBold,
      color: "var(--text-primary)",
    },
    criteriaRatingStyle: {
      height: "36px",
    },
  },
  mypRubricInnerContainerStyle: {
    height: "100%",
    width: "unset",
  },
};

class StudentMeasureAssessing extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.debouncedHandleAssessmentToolResponseInCache = _.debounce(
      props.handleAssessmentToolResponseInCache,
      300
    );
  }

  renderIBGradeScaleScore = assessmentToolType => {
    const { course, studentAssignmentData, evaluations, content } = this.props;

    const { ibPYPElementRatings, isSharedWithStudent } =
      evaluations || studentAssignmentData || {};
    const { assessmentTools } = content || {};

    const ibGradeScaleTool = getAssessmentToolItem(
      assessmentTools,
      assessmentToolType
    );

    const ibGradeScaleObj = getStudentScore({
      ibPYPElementRatings,
      assessmentToolType,
    });

    const ibGradeScaleScore = _.get(ibGradeScaleObj, "value", "");
    const academicCriteriaValues = _.get(
      ibGradeScaleTool,
      "academicCriteriaValues",
      []
    );

    const maxScore = _.size(academicCriteriaValues);

    // TOK has rating A to E so only show the rated alphabet
    const isTOK = _.get(course, "subjects.0.type") === "TOK";

    return (
      <div className={classes.studentScoreContainer}>
        <div className={classes.scoreContainer}>
          {ibGradeScaleScore && isSharedWithStudent ? (
            <>
              <span className={classes.studentScore}>{ibGradeScaleScore}</span>
              {!isTOK && maxScore > 0 && (
                <span className={classes.maxScore}>{`/${maxScore}`}</span>
              )}
            </>
          ) : (
            <>
              <span className={classes.dashText}>-</span>
              {!isTOK && maxScore > 0 && (
                <span className={classes.maxScore}>{` /${maxScore}`}</span>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  renderStudentScore = () => {
    const { studentAssignmentData, assessmentTools, evaluations } = this.props;

    const { ibPYPElementRatings, isSharedWithStudent } =
      evaluations || studentAssignmentData || {};

    const scoreObj = _.find(ibPYPElementRatings, { ibPYPElementType: SCORE });
    const studentScore = _.get(scoreObj, "value", "");

    const { maxScore } = getScoreDetailsMemoize({
      assessmentTools: {
        edges: assessmentTools,
      },
    });
    const _maxScore = _.round(maxScore, 2);

    return (
      <div className={classes.studentScoreContainer}>
        <div className={classes.scoreContainer}>
          {studentScore && isSharedWithStudent ? (
            <>
              <span className={classes.studentScore}>{studentScore}</span>
              <span className={classes.maxScore}>{`/${_maxScore}`}</span>
            </>
          ) : (
            <>
              <span className={classes.dashText}>-</span>
              <span className={classes.maxScore}>{` /${_maxScore}`}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  renderStudentScoreWithToolLabel = () => {
    const { studentAssignmentData, assessmentTools, evaluations } = this.props;

    const { ibPYPElementRatings } = evaluations || studentAssignmentData || {};

    const scoreObj = _.find(ibPYPElementRatings, { ibPYPElementType: SCORE });
    const studentScore = _.get(scoreObj, "value", "");

    const { maxScore } = getScoreDetailsMemoize({
      assessmentTools: {
        edges: assessmentTools,
      },
    });
    const _maxScore = _.round(maxScore, 2);

    return `${!_.isEmpty(studentScore) ? studentScore : "-"}/${_maxScore}`;
  };

  renderTeacherRemarkWithToolLabel = () => {
    const { studentAssignmentData, evaluations } = this.props;
    const { elementRemarks } = evaluations || studentAssignmentData || {};

    const commentMessage = getStaffElementRemarkMessage(elementRemarks);

    if (_.isEmpty(_.trim(commentMessage))) {
      return null;
    }

    return commentMessage;
  };

  renderGradeScaleScoreWithToolLabel = params => {
    const { studentAssignmentData, evaluations } = this.props;

    const { ibPYPElementRatings } =
      evaluations || studentAssignmentData || params || {};

    const gradeScaleToolRating = _.find(ibPYPElementRatings, {
      ibPYPElementType: GRADE_SCALE_TOOL,
    });

    return _.get(gradeScaleToolRating, "academicCriteriaValue.abbreviation");
  };

  renderIBGradeScaleScoreWithToolLabel = assessmentToolType => {
    const { course, studentAssignmentData, evaluations, content } = this.props;
    const { ibPYPElementRatings } = evaluations || studentAssignmentData || {};
    const { assessmentTools } = content || {};

    const ibGradeScaleTool = getAssessmentToolItem(
      assessmentTools,
      assessmentToolType
    );

    const ibGradeScaleObj = getStudentScore({
      ibPYPElementRatings,
      assessmentToolType,
    });

    const ibGradeScaleScore = _.get(ibGradeScaleObj, "value", "");
    const academicCriteriaValues = _.get(
      ibGradeScaleTool,
      "academicCriteriaValues",
      []
    );

    const maxScore = _.size(academicCriteriaValues);

    // TOK has rating A to E so only show the rated alphabet
    const isTOK = _.get(course, "subjects.0.type") === "TOK";
    const maxScoreToDisplay = !isTOK && maxScore > 0 ? `/${maxScore}` : "";

    return `${ibGradeScaleScore}${maxScoreToDisplay}`;
  };

  getEvaluationTextWithButtonVariant = params => {
    const {
      assessmentToolText,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
      assessmentToolType,
      assessmentToolId,
    } = params;

    const {
      t,
      isParent,
      studentAssignmentData,
      evaluations,
      isScheduled,
      isCurrentAcademicYearSelected,
      isSubmissionLockedForStudent,
      showToolsForStudentEvaluation,
      isArchivedSubmission = false,
    } = this.props;

    const { isEvaluationApplicable, isSubmitted } = studentAssignmentData || {};

    const { isEvaluated, isSharedWithStudent, toolResponses } =
      evaluations || studentAssignmentData || {};

    if (assessmentToolType === SCORE) {
      return { evaluationText: this.renderStudentScore() };
    }

    if (getIsIBGradeScaleTool({ assessmentToolType })) {
      return {
        evaluationText: this.renderIBGradeScaleScore(assessmentToolType),
      };
    }

    if (
      !isParent &&
      isStudentEvaluationEnable &&
      getIsEvaluationApplicable(isEvaluationApplicable) &&
      isCurrentAcademicYearSelected &&
      !(isScheduled || isSubmissionLockedForStudent || isArchivedSubmission)
    ) {
      const isEvaluationDone = getIsEvaluationDone({
        toolResponses,
        assessmentToolId,
      });

      if (isEvaluationDone) {
        return {
          evaluationText: showToolsForStudentEvaluation
            ? t("common:edit")
            : t("assessmentEvaluation:edit_evaluation"),
          icon: showToolsForStudentEvaluation ? <PencilLineOutlined /> : null,
        };
      } else {
        return {
          evaluationText: showToolsForStudentEvaluation
            ? t("common:add")
            : t("classRoom:evaluate_your_work"),
          buttonProps: {
            variant: "progressive",
            type: "fill",
          },
          icon: showToolsForStudentEvaluation ? <AddOutlined /> : null,
        };
      }
    } else if (
      (isStudentEvaluationEnable &&
        getIsEvaluationApplicable(isEvaluationApplicable) &&
        isSubmitted) ||
      (isTeacherEvaluationEnable && (isSharedWithStudent || isEvaluated))
    ) {
      return {
        evaluationText: t("common:view_with_label_lowercase", {
          label: t("common:evaluation"),
        }),
      };
    } else {
      return {
        evaluationText: t("common:view_with_label_lowercase", {
          label: assessmentToolText,
        }),
      };
    }
  };

  renderToolUI = node => {
    const {
      t,
      unitPlanFields,
      content,
      field_list,
      assessmentToolsEvaluationDetails,
      updateField,
      handleAssessmentToolResponseInCache,
      userType,
    } = this.props;

    const {
      assessmentToolType,
      assessmentToolItem: assessmentTool,
      isStudentEvaluationEnable,
    } = node || {};

    const {
      parentId,
      parentType,
      extraParams,
      submissionId,
      isSharedWithStudent,
      isEvaluationEnable,
    } = assessmentToolsEvaluationDetails;

    const canCurrentUserEvaluate =
      isStudentEvaluationEnable && isEvaluationEnable && userType === "student";

    const updateEvaluationCell = ({
      rowId,
      columnId,
      subjectiveResponse,
      type,
      assessmentToolId,
      isDebounce = false,
    }) => {
      // parent is not allowed to update rubric cell
      if (userType === "parent") {
        return;
      }

      let params = {
        parentId,
        parentType,
        rowId,
        type,
        columnId,
        subjectiveResponse,
        ..._.pick(extraParams, ["assignmentId", "sourceFrom", "courseId"]),
        submissionId,
      };

      if (assessmentToolId) {
        params = {
          ...params,
          assessmentToolId,
          isToolResponsesV2: true,
        };
      }

      if (isDebounce) {
        this.debouncedHandleAssessmentToolResponseInCache(params);
      } else {
        handleAssessmentToolResponseInCache(params);
      }

      updateField({
        key: `${rowId}_${columnId}`,
        params,
        type: "EVALUATION_TOOL",
        isDebounce: type == "SINGLE_POINT_RUBRIC",
      });
    };

    const currentToolEvaluationResponse = _.get(
      _.find(
        _.get(assessmentToolsEvaluationDetails, "toolEvaluationResponses", []),
        ({ toolId }) => toolId == _.get(node, "assessmentToolItem.id")
      ),
      "toolResponses",
      []
    );

    const assessmentToolStyles = canCurrentUserEvaluate
      ? getAssessmentToolSelfEvaluationModeStyles()
      : getAssessmentToolRevampedViewModeStyles();

    switch (assessmentToolType) {
      case "RUBRIC": {
        const evaluationProps = {
          isEvaluatedByStudent: isStudentEvaluationEnable,
          ...assessmentToolsEvaluationDetails,
          isEvaluation: canCurrentUserEvaluate,
          toolEvaluationResponses: currentToolEvaluationResponse,
          updateEvaluationCell: params =>
            updateEvaluationCell({
              ...params,
              assessmentToolId: _.get(node, "assessmentToolItem.id"),
              type: "RUBRIC",
            }),
          isSharedWithStudent,
          showRevampedEvaluationUI: true,
        };

        return (
          <RubricTable
            mode={"view"}
            rubric={assessmentTool}
            firstColumnHeaderText={t("common:criteria")}
            {...assessmentToolStyles.rubricStyles}
            showOnlyDefaultCursor={true}
            {...evaluationProps}
          />
        );
      }

      case "SINGLE_POINT_RUBRIC": {
        const evaluationProps = {
          isEvaluatedByStudent: isStudentEvaluationEnable,
          ...assessmentToolsEvaluationDetails,
          toolEvaluationResponses: currentToolEvaluationResponse,
          updateEvaluationCell: params =>
            updateEvaluationCell({
              ...params,
              assessmentToolId: _.get(node, "assessmentToolItem.id"),
              type: "SINGLE_POINT_RUBRIC",
            }),
          isEvaluation: true,
          isEvaluationEnable: canCurrentUserEvaluate,
          showRevampedEvaluationUI: true,
        };

        return (
          <SinglePointRubricTable
            mode={"view"}
            rubric={assessmentTool}
            {...assessmentToolStyles.singlePointRubricStyles}
            {...evaluationProps}
          />
        );
      }

      case "CHECKLIST": {
        const evaluationProps = {
          isEvaluatedByStudent: isStudentEvaluationEnable,
          ...assessmentToolsEvaluationDetails,
          isEvaluation: canCurrentUserEvaluate,
          toolEvaluationResponses: currentToolEvaluationResponse,
          updateEvaluationCell: params =>
            updateEvaluationCell({
              ...params,
              assessmentToolId: _.get(node, "assessmentToolItem.id"),
              type: "CHECKLIST",
            }),
          isSharedWithStudent,
          showRevampedEvaluationUI: true,
        };

        return (
          <ChecklistTable
            mode={"view"}
            checklist={assessmentTool}
            {...assessmentToolStyles.checklistStyles}
            {...evaluationProps}
            showOnlyDefaultCursor={true}
          />
        );
      }

      case "MYP_OBJECTIVE_RUBRIC":
      case "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC": {
        const evaluationProps = {
          ...assessmentToolsEvaluationDetails,
          isEvaluated: _.get(
            assessmentToolsEvaluationDetails,
            "isEvaluated",
            true
          ),
          isEvaluatedByStudent: isStudentEvaluationEnable,
          toolEvaluationResponses: currentToolEvaluationResponse,
          updateEvaluationCell: params =>
            updateEvaluationCell({
              ...params,
              assessmentToolId: _.get(node, "assessmentToolItem.id"),
              type: assessmentToolType,
            }),
          isEvaluationEnable: canCurrentUserEvaluate,
          showRevampedEvaluationUI: true,
        };

        return (
          <MYPObjectiveRubric
            assessmentTool={assessmentTool}
            mode={"view"}
            showObjectiveNameInTable={!canCurrentUserEvaluate}
            options={getMYPObjectiveRubricOptions({
              unitPlanFields,
              field_list,
              assessmentFields: _.get(content, "allFields", []),
              measureAssessingValue: assessmentToolType,
            })}
            {...assessmentToolStyles.mypObjectiveRubricStyles}
            {...evaluationProps}
            showOnlyDefaultCursor={true}
            innerContainerStyle={
              canCurrentUserEvaluate
                ? styles.mypRubricInnerContainerStyle
                : undefined
            }
          />
        );
      }

      case "DP_ASSESSMENT_RUBRIC": {
        const evaluationProps = {
          isEvaluationEnable: canCurrentUserEvaluate,
          ...assessmentToolsEvaluationDetails,
        };

        return (
          <CriteriaRubricAssessmentWrapper
            mode={"view"}
            criteria={assessmentTool?.criteria}
            {...assessmentToolStyles.dpRubricStyles}
            {...evaluationProps}
            customHeaderLabelClass={"text-label"}
            showToolIcon={true}
            showRevampedUI={true}
            marksLabelLocale={"classRoom:total_marks_with_score"}
            customSelectedRatingElement={classes.iconSelected}
            customRatingElementClass={classes.customRatingElementClass}
            assessmentTool={assessmentTool}
          />
        );
      }

      case SCORE_BASED_RUBRIC:
      case STANDARD_BASED_RUBRIC: {
        const _updateEvaluationCell = params => {
          updateEvaluationCell({
            ...params,
            assessmentToolId: _.get(node, "assessmentToolItem.id"),
            type: assessmentToolType,
          });
        };

        const evaluationProps = {
          isEvaluatedByStudent: isStudentEvaluationEnable,
          ...assessmentToolsEvaluationDetails,
          updateEvaluationCell: _updateEvaluationCell,
          toolEvaluationResponses: currentToolEvaluationResponse,
          isEvaluationEnable: canCurrentUserEvaluate,
        };

        return (
          <StandardRubric
            mode={"view"}
            assessmentTool={assessmentTool}
            {...assessmentToolStyles.standardRubricStyles}
            {...evaluationProps}
            type={assessmentToolType}
          />
        );
      }
      case GRADE_SCALE_TOOL: {
        return (
          <AssessmentGradeScaleTool
            mode={"view"}
            assessmentTool={assessmentTool}
            {...assessmentToolStyles.assessmentGradeScaleToolStyles}
            {...assessmentToolsEvaluationDetails}
            type={assessmentToolType}
          />
        );
      }

      case ASSESSMENT_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY: {
        return (
          <AssessmentIBScaleTool
            {...assessmentToolsEvaluationDetails}
            assessmentTool={assessmentTool}
            isEvaluationEnable={canCurrentUserEvaluate}
            toolStyles={styles.toolStyles}
            customSelectedRatingElement={classes.iconSelected}
            customRatingElementClass={classes.customRatingElementClass}
            showTitle={false}
            icon={<SortOutlined variant={"subtle"} size={"xxx-small"} />}
          />
        );
      }

      default:
        return null;
    }
  };

  renderAllTools = () => {
    const {
      assessmentTools,
      content,
      t,
      assessmentToolsEvaluationDetails,
    } = this.props;

    return _.map(
      reorderTools({
        assessmentTools,
        hasEdges: false,
      }),
      ({ node }) => {
        const { assessmentToolType, assessmentToolItem: assessmentTool } =
          node || {};

        const showAssessmentTool = getShowAssessmentTool({
          assessmentTool,
          assessmentToolType,
          allFields: _.get(content, "allFields", []),
        });

        const assessmentToolObj =
          _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }) || {};
        const toolLabel = _.get(assessmentToolObj, "locale", "");
        const toolIcon = _.get(
          ASSESSMENT_TOOL_ICON_MAPPING,
          assessmentToolType,
          null
        );

        const {
          isSharedWithStudent,
          ibPYPElementRatings,
          isTeacherEvaluationEnabled,
          commentMessage,
        } = assessmentToolsEvaluationDetails;

        const renderToolLabel = () => {
          switch (assessmentToolType) {
            case SCORE: {
              const { maxScore, isScoreEnabled } = getScoreDetailsMemoize({
                assessmentTools: {
                  edges: assessmentTools,
                },
              });
              const _maxScore = _.round(maxScore, 2);
              const isScoringEnabled =
                isTeacherEvaluationEnabled && isScoreEnabled && _maxScore;

              if (isScoringEnabled) {
                const scoreValue = isSharedWithStudent
                  ? getScoreValueElementRatings({
                      ibPYPElementRatings,
                    })
                  : null;

                return `${t(toolLabel)}: ${
                  _.isEmpty(scoreValue) ? "-" : scoreValue
                }/${_maxScore}`;
              }
              return `${t(toolLabel)}: -/${_maxScore}`;
            }
            case COMMENT: {
              const showTeacherComment =
                isSharedWithStudent &&
                isTeacherEvaluationEnabled &&
                !_.isEmpty(commentMessage);

              const textClasses = classNames({
                ["text-body"]: true,
                [classes.placeholderText]: true,
                [classes.italicText]: !showTeacherComment,
              });

              return (
                <>
                  {`${t(toolLabel)}: `}
                  <span className={textClasses}>
                    {showTeacherComment
                      ? commentMessage
                      : t("classRoom:teacher_comment_placeholder")}
                  </span>
                </>
              );
            }
            case GRADE_SCALE_TOOL: {
              const displayName = _.get(
                assessmentTool,
                "gradeScale.displayName"
              );

              const gradeScaleToolRating = this.renderGradeScaleScoreWithToolLabel(
                { ibPYPElementRatings }
              );

              const showGradeScaleEvaluation =
                isSharedWithStudent &&
                isTeacherEvaluationEnabled &&
                !_.isEmpty(gradeScaleToolRating);

              const textClasses = classNames({
                ["text-body"]: true,
                [classes.placeholderText]: true,
                [classes.italicText]: !showGradeScaleEvaluation,
              });

              return (
                <>
                  {`${t(displayName)}: `}
                  <span className={textClasses}>
                    {showGradeScaleEvaluation ? gradeScaleToolRating : "-"}
                  </span>
                </>
              );
            }
            default:
              return t(toolLabel);
          }
        };

        return showAssessmentTool ? (
          <div className={classes.toolContainer}>
            {!_.includes(
              TOOLS_WITH_LABEL_RENDERED_INSIDE_COMPONENT,
              assessmentToolType
            ) ? (
              <div className={classes.labelContainer}>
                <div className={classes.toolIconContainer}>{toolIcon}</div>
                <div className="text-label">{renderToolLabel()}</div>
              </div>
            ) : null}
            {!_.includes(
              [SCORE, COMMENT, GRADE_SCALE_TOOL],
              assessmentToolType
            ) ? (
              <div className={classes.toolUIWrapper}>
                <div className={classes.toolUIContainer}>
                  {this.renderToolUI(node)}
                </div>
              </div>
            ) : null}
          </div>
        ) : null;
      }
    );
  };

  renderToolMediaCard = node => {
    const {
      t,
      toggleAssessmentTool,
      assessmentTools,
      sourceFrom,
      isSubmissionLockedForStudent,
      showToolsWithTeacherEvaluation,
    } = this.props;
    const {
      assessmentToolType,
      assessmentToolItem: assessmentTool,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
    } = node || {};

    const assessmentToolId = _.get(assessmentTool, "id", "");

    const { assessmentToolText } = getAssessmentToolText({
      t,
      assessmentToolType,
      assessmentTool,
      showLabelAsIBGrade: true,
      sourceFrom,
    });

    const { type } = getAssessmentToolTypeForMediaCard({
      assessmentToolType,
      t,
    });
    const toolIcon = _.get(
      ASSESSMENT_TOOL_ICON_MAPPING,
      assessmentToolType,
      null
    );

    const showButton =
      assessmentToolType !== SCORE &&
      !getIsIBGradeScaleTool({ assessmentToolType });

    const {
      evaluationText,
      buttonProps = {},
      icon,
    } = this.getEvaluationTextWithButtonVariant({
      assessmentToolText,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
      assessmentToolType,
      assessmentToolId,
    });

    const onClickMediaCardButton = () => {
      const activeIndex = Math.max(
        0,
        _.findIndex(
          reorderTools({
            assessmentTools,
            hasEdges: false,
          }),
          tool =>
            _.get(tool, "node.assessmentToolItem.id") == assessmentToolId &&
            _.get(tool, "node.assessmentToolType") == assessmentToolType
        )
      );

      toggleAssessmentTool({
        assessmentToolId,
        assessmentToolType,
        isTeacherEvaluationEnable,
        isStudentEvaluationEnable,
        activeIndex,
      });
    };

    const options = showButton ? (
      <Button
        dsVersion="2.0"
        variant="neutral"
        type="outlined"
        size="small"
        onClick={onClickMediaCardButton}
        testId={`${this.props?.testId}-evalutionMediaCard`}
        {...buttonProps}
        icon={icon}
      >
        {evaluationText}
      </Button>
    ) : (
      evaluationText
    );

    const isIBGradeScaleTool = getIsIBGradeScaleTool({
      assessmentToolType,
    });

    const showSubtext =
      (_.includes([SCORE, COMMENT, GRADE_SCALE_TOOL], assessmentToolType) ||
        isIBGradeScaleTool) &&
      showToolsWithTeacherEvaluation;

    const renderSubtext = () => {
      if (isIBGradeScaleTool) {
        return this.renderIBGradeScaleScoreWithToolLabel(assessmentToolType);
      }

      switch (assessmentToolType) {
        case SCORE:
          return this.renderStudentScoreWithToolLabel();
        case COMMENT:
          return this.renderTeacherRemarkWithToolLabel();
        case GRADE_SCALE_TOOL:
          return _.get(assessmentTool, "gradeScale.displayName");
        default:
          return null;
      }
    };

    const renderLabel = () => {
      switch (assessmentToolType) {
        case GRADE_SCALE_TOOL:
          return this.renderGradeScaleScoreWithToolLabel();
        default:
          return assessmentToolText;
      }
    };

    return (
      <MediaCard
        dsVersion="2.0"
        type={type}
        name={renderLabel()}
        icon={React.cloneElement(toolIcon, { variant: "default" })}
        options={!isSubmissionLockedForStudent ? options : null}
        showSubtext={showSubtext}
        subtext={renderSubtext()}
        testId={this.props?.testId}
        url={""}
        onClick={onClickMediaCardButton}
      />
    );
  };

  renderToolsForStudentEvaluation = () => {
    const { assessmentTools, content } = this.props;

    return _.map(
      reorderTools({
        assessmentTools,
        hasEdges: false,
      }),
      ({ node }) => {
        const {
          assessmentToolType,
          isStudentEvaluationEnable,
          assessmentToolItem: assessmentTool,
        } = node || {};

        const showAssessmentTool = getShowAssessmentTool({
          assessmentTool,
          assessmentToolType,
          allFields: _.get(content, "allFields", []),
        });

        if (showAssessmentTool && isStudentEvaluationEnable) {
          return this.renderToolMediaCard(node);
        }
      }
    );
  };

  renderToolsWithTeacherEvaluation = () => {
    const {
      assessmentTools,
      content,
      showToolsForStudentEvaluation,
    } = this.props;

    return _.map(
      reorderTools({
        assessmentTools,
        hasEdges: false,
      }),
      ({ node }) => {
        const {
          assessmentToolType,
          isTeacherEvaluationEnable,
          isStudentEvaluationEnable,
          assessmentToolItem: assessmentTool,
        } = node || {};

        const showAssessmentTool = getShowAssessmentTool({
          assessmentTool,
          assessmentToolType,
          allFields: _.get(content, "allFields", []),
        });

        const showAssessmentToolCard =
          showAssessmentTool && showToolsForStudentEvaluation
            ? isStudentEvaluationEnable
            : isTeacherEvaluationEnable;

        if (showAssessmentToolCard) return this.renderToolMediaCard(node);
        else null;
      }
    );
  };

  renderEvaluationTool = node => {
    const {
      t,
      toggleAssessmentTool,
      assessmentTools,
      theatreViewAttachments,
      sourceFrom,
    } = this.props;
    const {
      assessmentToolType,
      assessmentToolItem: assessmentTool,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
    } = node || {};

    const assessmentToolId = _.get(assessmentTool, "id", "");

    const { assessmentToolText } = getAssessmentToolText({
      t,
      assessmentToolType,
      assessmentTool,
      showLabelAsIBGrade: true,
      sourceFrom,
    });

    const { icon = null, type } = getAssessmentToolTypeForMediaCard({
      assessmentToolType,
      t,
    });

    const showButton =
      assessmentToolType !== SCORE &&
      !getIsIBGradeScaleTool({ assessmentToolType });

    const { evaluationText } = this.getEvaluationTextWithButtonVariant({
      assessmentToolText,
      isTeacherEvaluationEnable,
      isStudentEvaluationEnable,
      assessmentToolType,
      assessmentToolId,
    });

    const onClickMediaCardButton = () => {
      const activeIndex =
        _.size(theatreViewAttachments) +
        Math.max(
          0,
          _.findIndex(
            assessmentTools,
            tool =>
              _.get(tool, "node.assessmentToolItem.id") == assessmentToolId &&
              _.get(tool, "node.assessmentToolType") == assessmentToolType
          )
        );
      toggleAssessmentTool({
        assessmentToolId,
        assessmentToolType,
        isTeacherEvaluationEnable,
        isStudentEvaluationEnable,
        activeIndex: activeIndex,
      });
    };

    const options = showButton ? (
      <Button
        dsVersion="2.0"
        variant="neutral"
        type="outlined"
        size="medium"
        onClick={onClickMediaCardButton}
        testId={`${this.props?.testId}-evalutionMediaCard`}
      >
        {evaluationText}
      </Button>
    ) : (
      evaluationText
    );

    return (
      <MediaCard
        cardType="medium"
        type={type}
        name={assessmentToolText}
        icon={icon}
        options={options}
        showSubtext={false}
        testId={this.props?.testId}
      />
    );
  };

  renderEvaluationSection = () => {
    const { assessmentTools, content } = this.props;

    return _.map(
      reorderTools({ assessmentTools, hasEdges: false }),
      ({ node }) => {
        const {
          assessmentToolType,
          isTeacherEvaluationEnable,
          isStudentEvaluationEnable,
          assessmentToolItem: assessmentTool,
        } = node || {};

        switch (assessmentToolType) {
          case COMMENT:
            return this.renderTeacherRemark();
          default: {
            const showAssessmentTool = getShowAssessmentTool({
              assessmentTool,
              assessmentToolType,
              allFields: _.get(content, "allFields", []),
            });

            const showAssessmentToolCard =
              showAssessmentTool &&
              (isTeacherEvaluationEnable || isStudentEvaluationEnable);
            if (showAssessmentToolCard) return this.renderEvaluationTool(node);
            else null;
          }
        }
      }
    );
  };

  renderTeacherRemark = () => {
    const { t, studentAssignmentData, evaluations } = this.props;
    const { isSharedWithStudent, elementRemarks } =
      evaluations || studentAssignmentData || {};

    const commentMessage = getStaffElementRemarkMessage(elementRemarks);

    return !_.isEmpty(_.trim(commentMessage)) && isSharedWithStudent ? (
      <div className={classes.teacherRemarkContainer}>
        <div className={classes.remark}>
          <div className={classes.teacherRemarkTextContainer}>
            <CommentRemarkOutlined size="xx-small" />
            <span className={classes.teacherRemarkText}>
              {t("common:final_remarks")}
            </span>
          </div>
          {commentMessage}
        </div>
      </div>
    ) : null;
  };

  shouldShowEmpty = () => {
    const {
      showAssessmentEvaluationTool,
      showAllTools = false,
      showToolsForStudentEvaluation = false,
      showToolsWithTeacherEvaluation = false,
    } = this.props;

    return !(
      showAssessmentEvaluationTool ||
      showAllTools ||
      showToolsForStudentEvaluation ||
      showToolsWithTeacherEvaluation
    );
  };

  renderView = () => {
    const {
      showAllTools = false,
      showToolsForStudentEvaluation = false,
      showToolsWithTeacherEvaluation = false,
    } = this.props;

    const multipleToolsContainerClasses = classNames({
      [classes.multipleToolsContainer]: true,
      [classes.multipleToolsContainerV2]:
        showToolsForStudentEvaluation || showToolsWithTeacherEvaluation,
    });

    return (
      <div className={multipleToolsContainerClasses}>
        {showAllTools
          ? this.renderAllTools()
          : showToolsForStudentEvaluation
          ? this.renderToolsForStudentEvaluation()
          : showToolsWithTeacherEvaluation
          ? this.renderToolsWithTeacherEvaluation()
          : this.renderEvaluationSection()}
      </div>
    );
  };
}

const mapActionCreators = {
  updateField,
  handleAssessmentToolResponseInCache,
};

const mapStateToProps = state => {
  const isCurrentAcademicYearSelected = getIsCurrentAcademicYearSelected(state);
  const isParent = getIsParent(state);
  const userType = getUserType(state);

  return {
    isCurrentAcademicYearSelected,
    isParent,
    userType,
  };
};

export default compose(
  I18nHOC({ resources: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(StudentMeasureAssessing);
